import { useEffect, useState, useMemo, useRef } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import api, { formatEur, formatEur2, formatDate } from "@/lib/api";
import { ChevronRight, Users, FolderKanban, FileText, Wallet, AlertCircle, ArrowLeft, Plus, Mail, Phone, ExternalLink, Pencil, X, Globe, Trash2, Percent, Check, FileDown } from "lucide-react";
import ContactDetailModal from "@/components/ContactDetailModal";

export function FranchiseursList() {
  const [items, setItems] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [mergeOpen, setMergeOpen] = useState(false);

  const load = async () => {
    const { data } = await api.get("/franchiseurs/stats");
    setItems(data.items || []);
  };
  useEffect(() => { load(); }, []);

  const toggleSel = (fr) => {
    const next = new Set(selected);
    if (next.has(fr)) next.delete(fr); else next.add(fr);
    setSelected(next);
  };

  const downloadProforma = async (nom) => {
    try {
      const res = await api.get(`/franchiseurs/proforma.pdf?franchiseur=${encodeURIComponent(nom)}`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url; a.download = `proforma_retro_${nom.replace(/\s+/g, "_")}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      alert("Échec génération proforma : " + (e.response?.data?.detail || e.message));
    }
  };

  const doMerge = async (target) => {
    const sources = Array.from(selected).filter((s) => s !== target);
    if (!sources.length) return alert("Sélectionnez au moins 2 franchiseurs.");
    const res = await api.post("/franchiseurs/merge", { sources, target });
    alert(`✅ ${res.data.projects_updated} projet(s) re-rattaché(s) à « ${target} ».`);
    setSelected(new Set()); setMergeOpen(false); load();
  };

  const saveTaux = async (item, taux) => {
    if (!item.id) {
      // Pas encore d'entrée dans la collection franchiseurs : upsert
      await api.post("/franchiseurs/upsert-by-name", { nom: item.franchiseur, taux_retrocommission: taux });
    } else {
      await api.put(`/franchiseurs/${item.id}`, { taux_retrocommission: taux });
    }
    load();
  };

  const deleteFr = async (nom) => {
    if (!confirm(`Supprimer « ${nom} » de la liste des franchiseurs ?\nLes projets/marques liés repasseront en « Indépendant ».`)) return;
    await api.delete(`/franchiseurs/by-name/${encodeURIComponent(nom)}`);
    setSelected(new Set());
    load();
  };

  const renameFr = async (nom) => {
    const nouveau = prompt(`Renommer le franchiseur « ${nom} » :`, nom);
    if (!nouveau || nouveau.trim() === "" || nouveau.trim() === nom) return;
    await api.post("/franchiseurs/merge", { sources: [nom], target: nouveau.trim() });
    load();
  };

  return (
    <div className="space-y-6" data-testid="franchiseurs-page">
      <div className="flex items-end justify-between flex-wrap gap-2">
        <div>
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-stone-500">Réseau</div>
          <h1 className="font-display text-4xl font-bold tracking-tighter mt-1">Franchiseurs</h1>
          <p className="text-sm text-stone-500 mt-1">{items.length} réseau(x) · le taux % alimente automatiquement les rétrocommissions HT.</p>
        </div>
        {selected.size >= 2 && (
          <button data-testid="fr-merge-open" onClick={() => setMergeOpen(true)} className="bg-stone-900 text-white px-4 py-2 rounded-lg text-sm font-semibold">Fusionner ({selected.size}) →</button>
        )}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {items.map((f) => {
          const sel = selected.has(f.franchiseur);
          return (
            <div key={f.franchiseur} className={`card-flat p-4 transition relative ${sel ? "border-[var(--ppd)] ring-2 ring-[var(--ppd)]/30" : "hover:border-[var(--ppd)]/40"}`} data-testid={`fr-card-${f.franchiseur}`}>
              <button onClick={() => toggleSel(f.franchiseur)} data-testid={`fr-sel-${f.franchiseur}`} className="absolute top-2 right-2 w-5 h-5 rounded border border-stone-300 flex items-center justify-center bg-white">
                {sel && <span className="text-[var(--ppd)] font-bold">✓</span>}
              </button>
              {f.franchiseur !== "Indépendant" && (
                <button onClick={(e) => { e.preventDefault(); deleteFr(f.franchiseur); }} data-testid={`fr-del-${f.franchiseur}`} title="Supprimer ce franchiseur" className="absolute top-2 right-9 text-stone-300 hover:text-rose-600">
                  <Trash2 size={13} />
                </button>
              )}
              <Link to={`/franchiseurs/${encodeURIComponent(f.franchiseur)}`}>
                <div className="flex items-start justify-between gap-2 mb-3 pr-7">
                  <div>
                    <div className="font-display font-bold text-lg flex items-center gap-1.5">
                    {f.franchiseur}
                    {f.franchiseur !== "Indépendant" && (
                      <button type="button" title="Renommer le franchiseur" data-testid={`fr-rename-${f.franchiseur}`}
                        onClick={(e) => { e.preventDefault(); e.stopPropagation(); renameFr(f.franchiseur); }}
                        className="p-0.5 text-stone-300 hover:text-stone-700 transition-colors">
                        <Pencil size={13} />
                      </button>
                    )}
                  </div>
                    <div className="text-xs text-stone-500">{f.projets} projet{f.projets > 1 ? "s" : ""} · {f.actifs} actif{f.actifs > 1 ? "s" : ""}</div>
                  </div>
                  {f.en_retard > 0 && <span className="pill bg-stone-900 text-white text-[10px]"><AlertCircle size={9} className="inline mr-0.5 -mt-0.5"/>{f.en_retard} retard</span>}
                </div>
              </Link>
              {/* Taux rétrocommission inline */}
              {f.franchiseur !== "Indépendant" && (
                <div className="flex items-center gap-2 mb-2 p-2 bg-stone-50 rounded">
                  <Percent size={12} className="text-stone-400"/>
                  <span className="text-[10px] uppercase tracking-widest text-stone-500 font-bold">Rétro</span>
                  <TauxEditor value={f.taux_retrocommission || 0} onSave={(v) => saveTaux(f, v)} testid={`fr-taux-${f.franchiseur}`}/>
                  <span className="text-[10px] text-stone-500">%</span>
                  {(f.retro_total || 0) > 0 && (
                    <span className="ml-auto text-[10px] font-mono text-stone-600">{formatEur(f.retro_total)}</span>
                  )}
                </div>
              )}
              {f.franchiseur !== "Indépendant" && (f.retro_due || 0) > 0 && (
                <button type="button" onClick={(e) => { e.preventDefault(); e.stopPropagation(); downloadProforma(f.franchiseur); }}
                  data-testid={`fr-proforma-${f.franchiseur}`}
                  className="w-full mb-2 flex items-center justify-center gap-1.5 text-xs font-semibold border border-[var(--ppd)] text-[var(--ppd)] hover:bg-[var(--ppd)] hover:text-white px-3 py-1.5 rounded transition-colors">
                  <FileDown size={13}/> Proforma dues ({formatEur(f.retro_due)})
                </button>
              )}
              <Link to={`/franchiseurs/${encodeURIComponent(f.franchiseur)}`}>
                <div className="grid grid-cols-2 gap-2 text-xs">
                  <div><div className="text-[10px] uppercase tracking-widest text-stone-400 font-bold">CA vendu</div><div className="font-mono font-semibold">{formatEur(f.ca_vendu)}</div></div>
                  <div><div className="text-[10px] uppercase tracking-widest text-stone-400 font-bold">CA encaissé</div><div className="font-mono font-semibold text-[var(--ppd)]">{formatEur(f.ca_encaisse)}</div></div>
                  <div><div className="text-[10px] uppercase tracking-widest text-stone-400 font-bold">Marge HT</div><div className="font-mono font-semibold">{formatEur(f.marge)}</div></div>
                  <div><div className="text-[10px] uppercase tracking-widest text-stone-400 font-bold">Temps</div><div className="font-mono font-semibold">{f.temps_h} h</div></div>
                </div>
                {f.demarches_en_cours > 0 && <div className="text-[11px] text-amber-700 mt-2">{f.demarches_en_cours} démarche(s) en cours</div>}
                <div className="mt-3 text-[11px] text-stone-400 flex items-center gap-1">Voir détail <ChevronRight size={11}/></div>
              </Link>
            </div>
          );
        })}
        {items.length === 0 && <div className="col-span-full text-center text-stone-400 py-12">Aucun franchiseur.</div>}
      </div>

      {mergeOpen && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setMergeOpen(false)}>
          <div className="bg-white rounded-2xl p-6 max-w-md w-full" onClick={(e) => e.stopPropagation()} data-testid="fr-merge-dialog">
            <h3 className="font-display text-xl font-bold mb-4">Fusionner {selected.size} franchiseurs</h3>
            <p className="text-sm text-stone-600 mb-3">Choisissez le nom cible. Tous les projets seront re-rattachés sous ce nom.</p>
            <div className="space-y-2">
              {Array.from(selected).map((fr) => (
                <button key={fr} onClick={() => doMerge(fr)} data-testid={`fr-merge-target-${fr}`} className="w-full text-left p-3 border border-stone-200 rounded-lg hover:border-stone-900 hover:bg-stone-50 font-semibold">
                  Tout fusionner vers « <span className="text-[var(--ppd)]">{fr}</span> »
                </button>
              ))}
              <button onClick={() => {
                const v = prompt("Nom cible personnalisé (ex: PMS Groupe) :", "");
                if (v?.trim()) doMerge(v.trim());
              }} data-testid="fr-merge-custom" className="w-full text-left p-3 border border-dashed border-stone-300 rounded-lg hover:border-stone-900 text-sm">
                ✏️ Nom personnalisé…
              </button>
            </div>
            <button onClick={() => setMergeOpen(false)} className="mt-4 w-full text-stone-500 text-sm">Annuler</button>
          </div>
        </div>
      )}
    </div>
  );
}

function TauxEditor({ value, onSave, testid }) {
  // Input TOUJOURS visible (pas de mode "edit") pour éliminer toute perte de saisie.
  // Sauvegarde automatique 500ms après la dernière frappe + sauvegarde immédiate au blur.
  const [v, setV] = useState(Number(value || 0));
  const [savedFlash, setSavedFlash] = useState(false);
  const timerRef = useRef(null);
  const lastSavedRef = useRef(Number(value || 0));

  // Synchronise quand la prop change (après reload du parent)
  useEffect(() => {
    setV(Number(value || 0));
    lastSavedRef.current = Number(value || 0);
  }, [value]);

  const persist = async (nv) => {
    const n = Number(nv);
    if (Number.isNaN(n)) return;
    if (n === lastSavedRef.current) return;
    lastSavedRef.current = n;
    await onSave(n);
    setSavedFlash(true);
    setTimeout(() => setSavedFlash(false), 1200);
  };

  const handleChange = (e) => {
    const nv = e.target.value;
    setV(nv);
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => persist(nv), 500);
  };

  const handleBlur = () => {
    if (timerRef.current) clearTimeout(timerRef.current);
    persist(v);
  };

  return (
    <span className="inline-flex items-center gap-1">
      <input
        type="number" step="0.1" min="0" max="100"
        value={v}
        onChange={handleChange}
        onBlur={handleBlur}
        onKeyDown={(e) => {
          if (e.key === "Enter") { e.preventDefault(); handleBlur(); }
        }}
        onClick={(e) => { e.stopPropagation(); e.preventDefault(); }}
        onMouseDown={(e) => e.stopPropagation()}
        className="w-14 border border-stone-300 hover:border-[var(--ppd)] focus:border-[var(--ppd)] focus:outline-none rounded px-1 py-0.5 text-xs font-mono font-bold bg-white text-stone-900"
        data-testid={testid}
      />
      {savedFlash && <span className="text-emerald-600 text-[10px]" data-testid={`${testid}-saved`}>✓</span>}
    </span>
  );
}

export default function FranchiseurDetail() {
  const { nom } = useParams();
  const [searchParams] = useSearchParams();
  const [stats, setStats] = useState(null);
  const [net, setNet] = useState(null);
  const [tab, setTab] = useState(searchParams.get("tab") || "contacts");
  const [detail, setDetail] = useState(null);
  const [showAddContact, setShowAddContact] = useState(false);
  const [taux, setTaux] = useState(0);
  const [newEns, setNewEns] = useState("");

  const load = async () => {
    const [s, n] = await Promise.all([
      api.get("/franchiseurs/stats"),
      api.get(`/franchiseurs/by-name/${encodeURIComponent(nom)}/network`),
    ]);
    const item = (s.data.items || []).find((x) => x.franchiseur === nom);
    setStats(item);
    setNet(n.data);
    setTaux(item?.taux_retrocommission || 0);
  };
  useEffect(() => { load(); }, [nom]);

  const saveTaux = async (v) => {
    if (stats?.id) {
      await api.put(`/franchiseurs/${stats.id}`, { taux_retrocommission: v });
    } else {
      await api.post("/franchiseurs/upsert-by-name", { nom, taux_retrocommission: v });
    }
    setTaux(v); load();
  };

  const detach = async (cid) => {
    if (!confirm("Détacher ce contact du franchiseur ?")) return;
    await api.post(`/franchiseurs/by-name/${encodeURIComponent(nom)}/contacts/detach`, { contact_id: cid });
    load();
  };

  const attachEnseigne = async () => {
    const v = newEns.trim();
    if (!v) return;
    await api.post(`/franchiseurs/by-name/${encodeURIComponent(nom)}/enseignes/attach`, { enseigne: v });
    setNewEns(""); load();
  };
  const detachEnseigne = async (ens) => {
    if (!confirm(`Retirer la marque « ${ens} » de ce franchiseur ?`)) return;
    await api.post(`/franchiseurs/by-name/${encodeURIComponent(nom)}/enseignes/detach`, { enseigne: ens });
    load();
  };

  if (!stats || !net) return <div className="text-sm text-stone-500" data-testid="fr-detail-loading">Chargement…</div>;

  const counts = {
    contacts: net.contacts.length,
    fournisseurs: net.fournisseurs.length,
    prestataires: net.prestataires.length,
    projets: net.projets.length,
    retrocommissions: net.retrocommissions.length,
  };

  return (
    <div className="space-y-6" data-testid="fr-detail-page">
      <div>
        <Link to="/franchiseurs" className="text-xs text-stone-500 hover:text-stone-900 flex items-center gap-1 mb-2"><ArrowLeft size={11}/> Retour aux franchiseurs</Link>
        <div className="text-xs font-bold uppercase tracking-[0.2em] text-stone-500">Réseau franchise</div>
        <h1 className="font-display text-4xl font-bold tracking-tighter mt-1">{nom}</h1>
        <p className="text-sm text-stone-500 mt-1">
          {stats.projets} projet{stats.projets > 1 ? "s" : ""} · {stats.actifs} actif{stats.actifs > 1 ? "s" : ""} · {stats.abandonnes} abandonné{stats.abandonnes > 1 ? "s" : ""}
          {net.enseignes.length > 0 && <> · Enseignes : <span className="font-semibold text-stone-700">{net.enseignes.join(", ")}</span></>}
        </p>
      </div>

      {/* Taux rétrocommission - édition principale */}
      {nom !== "Indépendant" && (
        <div className="card-flat p-4 flex flex-wrap items-center gap-4" data-testid="fr-taux-edit-box">
          <div className="flex items-center gap-2">
            <Percent size={16} className="text-[var(--ppd)]"/>
            <span className="text-[10px] uppercase tracking-widest text-stone-500 font-bold">Taux rétrocommission</span>
          </div>
          <TauxEditor value={taux} onSave={saveTaux} testid="fr-detail-taux"/>
          <span className="text-sm font-semibold text-stone-700">%</span>
          <span className="text-xs text-stone-500 ml-2">Appliqué automatiquement à tous les projets liés. Recalcule la rétrocommission HT sur le montant vendu.</span>
        </div>
      )}

      {/* Marques / Enseignes associées */}
      {nom !== "Indépendant" && (
        <div className="card-flat p-4" data-testid="fr-enseignes-box">
          <div className="flex items-center gap-2 mb-3">
            <FolderKanban size={16} className="text-[var(--ppd)]" />
            <span className="text-[10px] uppercase tracking-widest text-stone-500 font-bold">Marques / Enseignes associées</span>
          </div>
          <div className="flex flex-wrap gap-2 mb-3">
            {(net.enseignes || []).length === 0 && <span className="text-xs text-stone-400 italic">Aucune marque associée.</span>}
            {(net.enseignes || []).map((ens) => (
              <span key={ens} data-testid={`fr-ens-chip-${ens}`} className="pill bg-stone-100 text-stone-700 flex items-center gap-1.5">
                {ens}
                <button onClick={() => detachEnseigne(ens)} data-testid={`fr-ens-remove-${ens}`} title="Retirer" className="text-stone-400 hover:text-rose-600"><X size={11} /></button>
              </span>
            ))}
          </div>
          <div className="flex gap-2 items-center">
            <input
              value={newEns}
              onChange={(e) => setNewEns(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); attachEnseigne(); } }}
              placeholder="Ajouter une marque (ex. Crousty Game)"
              data-testid="fr-ens-input"
              className="border border-stone-200 rounded px-2 py-1.5 text-sm flex-1 min-w-[160px] focus:border-stone-900 outline-none"
            />
            <button onClick={attachEnseigne} data-testid="fr-ens-add" className="bg-stone-900 text-white px-3 py-1.5 rounded text-xs font-semibold flex items-center gap-1.5"><Plus size={12} /> Associer</button>
          </div>
        </div>
      )}

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
        <Kpi label="Projets" value={stats.projets}/>
        <Kpi label="CA vendu HT" value={formatEur(stats.ca_vendu)} mono/>
        <Kpi label="CA encaissé HT" value={formatEur(stats.ca_encaisse)} mono accent="emerald"/>
        <Kpi label="Marge HT" value={formatEur(stats.marge)} mono/>
        <Kpi label="Rétro à venir" value={formatEur(stats.retro_a_venir || 0)} mono accent="stone"/>
        <Kpi label="Rétro due" value={formatEur(stats.retro_due || 0)} mono accent="amber"/>
        <Kpi label="Rétro payée" value={formatEur(stats.retro_payee || 0)} mono accent="emerald"/>
      </div>

      {/* Onglets */}
      <div className="flex border-b border-stone-200 flex-wrap">
        {[
          ["contacts", `Contacts (${counts.contacts})`, Users],
          ["fournisseurs", `Fournisseurs (${counts.fournisseurs})`, FolderKanban],
          ["prestataires", `Prestataires (${counts.prestataires})`, Users],
          ["projets", `Projets (${counts.projets})`, FolderKanban],
          ["retrocommissions", `Rétrocommissions (${counts.retrocommissions})`, Wallet],
        ].map(([id, l, Icon]) => (
          <button key={id} data-testid={`fr-tab-${id}`} onClick={() => setTab(id)}
                  className={`px-3 py-2 text-sm font-semibold border-b-2 ${tab === id ? "border-stone-900 text-stone-900" : "border-transparent text-stone-500"} flex items-center gap-2`}>
            <Icon size={14}/> {l}
          </button>
        ))}
      </div>

      {tab === "contacts" && (
        <div className="space-y-3">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <p className="text-xs text-stone-500">Rattachement auto : société identique, projets associés, ou rattachement manuel.</p>
            <button data-testid="fr-add-contact" onClick={() => setShowAddContact(true)} className="bg-stone-900 text-white px-3 py-1.5 rounded text-xs font-semibold flex items-center gap-1.5"><Plus size={12}/> Nouveau contact</button>
          </div>
          {net.contacts.length === 0 && <div className="text-center text-stone-400 py-12 italic text-sm">Aucun contact rattaché à ce franchiseur.</div>}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {net.contacts.map((c) => <ContactCard key={c.id} c={c} onOpen={() => setDetail(c)} onDetach={c.rattachement.includes("manuel") ? () => detach(c.id) : null}/>)}
          </div>
        </div>
      )}

      {tab === "fournisseurs" && (
        <NetworkBucket items={net.fournisseurs} onOpen={(c) => setDetail(c)} emptyLabel="Aucun fournisseur référencé sur ce réseau."/>
      )}

      {tab === "prestataires" && (
        <NetworkBucket items={net.prestataires} onOpen={(c) => setDetail(c)} emptyLabel="Aucun prestataire référencé sur ce réseau."/>
      )}

      {tab === "projets" && (
        <div className="space-y-2">
          {net.projets.length === 0 && <div className="text-stone-400 text-center py-8 italic">Aucun projet.</div>}
          {net.projets.map((p) => (
            <Link key={p.id} to={`/projets/${p.id}`} className="card-flat p-3 flex items-center gap-3 hover:border-[var(--ppd)]/40" data-testid={`fr-proj-${p.id}`}>
              <div className="flex-1 min-w-0">
                <div className="font-semibold truncate">{p.nom}</div>
                <div className="text-xs text-stone-500">{p.client} · {p.ville || "—"}</div>
              </div>
              <span className="pill bg-stone-100 text-stone-700 text-[10px]">{p.status}</span>
              <span className="font-mono text-sm">{formatEur(p.montant_vendu)}</span>
              {(p.retrocommission || 0) > 0 && (
                <span className="pill bg-rose-50 text-rose-700 text-[10px] font-mono">−{formatEur(p.retrocommission)}</span>
              )}
              <ChevronRight size={13} className="text-stone-300"/>
            </Link>
          ))}
        </div>
      )}

      {tab === "retrocommissions" && (
        <RetrocommissionsTab data={net} onReload={load}/>
      )}

      {detail && (
        <ContactDetailModal
          item={detail}
          onClose={() => setDetail(null)}
          onSaved={() => { setDetail(null); load(); }}
          onDeleted={() => { setDetail(null); load(); }}
        />
      )}

      {showAddContact && (
        <AddContactModal
          franchiseur={nom}
          onClose={() => setShowAddContact(false)}
          onSaved={() => { setShowAddContact(false); load(); }}
        />
      )}
    </div>
  );
}

function ContactCard({ c, onOpen, onDetach }) {
  return (
    <div className="card-flat p-3 hover:border-[var(--ppd)]/40 transition" data-testid={`fr-contact-${c.id}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="flex-1 min-w-0 cursor-pointer" onClick={onOpen}>
          <div className="font-semibold truncate">{c.nom}</div>
          {c.poste && <div className="text-xs text-stone-500 truncate">{c.poste}</div>}
          {c.societe && c.societe !== c.nom && <div className="text-xs text-stone-500 truncate italic">{c.societe}</div>}
        </div>
        <div className="flex flex-col items-end gap-1 shrink-0">
          <div className="flex gap-0.5">
            {(c.types || []).map((t) => <span key={t} className="pill bg-stone-100 text-stone-600 text-[9px] capitalize">{t}</span>)}
          </div>
          <div className="flex gap-0.5 flex-wrap justify-end">
            {(c.rattachement || []).map((r) => <span key={r} className="pill bg-amber-50 text-amber-800 text-[9px]">{r}</span>)}
          </div>
        </div>
      </div>
      <div className="mt-2 flex items-center gap-1.5 flex-wrap text-[11px]">
        {c.email && <a onClick={(e) => e.stopPropagation()} href={`mailto:${c.email}`} className="inline-flex items-center gap-1 text-stone-600 hover:text-stone-900 px-1.5 py-0.5 border border-stone-200 rounded"><Mail size={10}/> {c.email}</a>}
        {c.telephone && <a onClick={(e) => e.stopPropagation()} href={`tel:${c.telephone.replace(/\s/g, '')}`} className="inline-flex items-center gap-1 text-stone-600 hover:text-stone-900 px-1.5 py-0.5 border border-stone-200 rounded"><Phone size={10}/> {c.telephone}</a>}
        {c.dernier_contact && <span className="text-stone-400">↺ {formatDate(c.dernier_contact)}</span>}
      </div>
      {onDetach && (
        <button onClick={onDetach} className="mt-2 text-[10px] text-stone-400 hover:text-rose-700 flex items-center gap-1"><X size={9}/> Détacher manuel</button>
      )}
      {(c.derniers_projets || []).length > 0 && (
        <div className="mt-1.5 text-[10px] text-stone-500">Derniers projets : {c.derniers_projets.join(" · ")}</div>
      )}
    </div>
  );
}

function NetworkBucket({ items, onOpen, emptyLabel }) {
  return (
    <div className="space-y-3">
      {items.length === 0 && <div className="text-center text-stone-400 py-12 italic text-sm">{emptyLabel}</div>}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {items.map((c) => (
          <div key={c.id} className="card-flat p-3 hover:border-[var(--ppd)]/40 cursor-pointer" data-testid={`fr-bucket-${c.id}`} onClick={() => onOpen(c)}>
            <div className="flex items-start justify-between gap-2">
              <div className="flex-1 min-w-0">
                <div className="font-semibold truncate">{c.societe || c.nom}</div>
                <div className="text-xs text-stone-500 truncate">{c.metier || c.categorie || "—"}</div>
                {c.zone && <div className="text-[10px] text-stone-400">{c.zone}</div>}
              </div>
              <div className="flex flex-col items-end gap-1">
                {(c.rattachement || []).slice(0, 2).map((r) => <span key={r} className="pill bg-amber-50 text-amber-800 text-[9px]">{r}</span>)}
              </div>
            </div>
            <div className="mt-2 flex flex-wrap gap-1.5 text-[11px]">
              {c.email && <a onClick={(e) => e.stopPropagation()} href={`mailto:${c.email}`} className="inline-flex items-center gap-1 text-stone-600 hover:text-stone-900 px-1.5 py-0.5 border border-stone-200 rounded"><Mail size={10}/></a>}
              {c.telephone && <a onClick={(e) => e.stopPropagation()} href={`tel:${c.telephone.replace(/\s/g, '')}`} className="inline-flex items-center gap-1 text-stone-600 hover:text-stone-900 px-1.5 py-0.5 border border-stone-200 rounded"><Phone size={10}/></a>}
              {c.site && <a onClick={(e) => e.stopPropagation()} href={c.site} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-stone-600 hover:text-stone-900 px-1.5 py-0.5 border border-stone-200 rounded"><Globe size={10}/></a>}
            </div>
            {c.remises && <div className="mt-2 text-[11px] text-emerald-700">Remise : {c.remises}</div>}
            {(c.derniers_projets || []).length > 0 && (
              <div className="mt-1.5 text-[10px] text-stone-500">Derniers projets : {c.derniers_projets.join(" · ")}</div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function AddContactModal({ franchiseur, onClose, onSaved }) {
  const [form, setForm] = useState({ nom: "", societe: franchiseur, poste: "", email: "", telephone: "", types: ["client"] });
  const [saving, setSaving] = useState(false);
  const toggleType = (t) => {
    setForm((f) => {
      const s = new Set(f.types);
      s.has(t) ? s.delete(t) : s.add(t);
      return { ...f, types: Array.from(s.size ? s : new Set(["client"])) };
    });
  };
  const submit = async (e) => {
    e.preventDefault();
    if (!form.nom.trim()) return alert("Nom requis");
    setSaving(true);
    try {
      const { data: created } = await api.post("/contacts", { ...form });
      // Rattachement manuel au franchiseur
      await api.post(`/franchiseurs/by-name/${encodeURIComponent(franchiseur)}/contacts/attach`, { contact_id: created.id });
      onSaved?.();
    } finally { setSaving(false); }
  };
  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <form onSubmit={submit} className="bg-white rounded-2xl p-6 max-w-lg w-full" onClick={(e) => e.stopPropagation()} data-testid="fr-add-contact-form">
        <h3 className="font-display text-2xl font-bold mb-1">Nouveau contact</h3>
        <p className="text-xs text-stone-500 mb-4">Rattaché automatiquement à <span className="font-semibold text-stone-700">{franchiseur}</span></p>
        <div className="space-y-2 text-sm">
          <input required value={form.nom} onChange={(e) => setForm({...form, nom: e.target.value})} placeholder="Nom *" className="w-full border border-stone-200 rounded-lg px-3 py-2" data-testid="fr-ac-nom"/>
          <input value={form.societe} onChange={(e) => setForm({...form, societe: e.target.value})} placeholder="Société" className="w-full border border-stone-200 rounded-lg px-3 py-2" data-testid="fr-ac-societe"/>
          <input value={form.poste} onChange={(e) => setForm({...form, poste: e.target.value})} placeholder="Poste / fonction (ex: Développeur réseau)" className="w-full border border-stone-200 rounded-lg px-3 py-2" data-testid="fr-ac-poste"/>
          <input type="email" value={form.email} onChange={(e) => setForm({...form, email: e.target.value})} placeholder="Email" className="w-full border border-stone-200 rounded-lg px-3 py-2" data-testid="fr-ac-email"/>
          <input value={form.telephone} onChange={(e) => setForm({...form, telephone: e.target.value})} placeholder="Téléphone" className="w-full border border-stone-200 rounded-lg px-3 py-2" data-testid="fr-ac-tel"/>
          <div className="pt-2">
            <div className="text-[10px] uppercase tracking-widest font-bold text-stone-500 mb-1">Type(s)</div>
            <div className="flex gap-1.5 flex-wrap">
              {["client","partenaire","fournisseur","prestataire","prospect"].map((t) => (
                <button type="button" key={t} onClick={() => toggleType(t)} className={`px-2.5 py-1 rounded-full text-xs font-semibold border ${form.types.includes(t) ? "bg-stone-900 text-white border-stone-900" : "bg-white text-stone-700 border-stone-200"}`}>{t}</button>
              ))}
            </div>
          </div>
        </div>
        <div className="flex justify-end gap-2 mt-5">
          <button type="button" onClick={onClose} className="px-4 py-2 text-sm border border-stone-200 rounded-lg">Annuler</button>
          <button type="submit" disabled={saving} data-testid="fr-ac-submit" className="px-4 py-2 text-sm bg-stone-900 text-white rounded-lg font-semibold flex items-center gap-1.5"><Check size={14}/> Créer & rattacher</button>
        </div>
      </form>
    </div>
  );
}

function RetroStatusPill({ status }) {
  const cfg = {
    a_venir: { label: "À venir", cls: "bg-stone-100 text-stone-700" },
    due: { label: "Due", cls: "bg-amber-50 text-amber-800" },
    payee: { label: "Payée", cls: "bg-emerald-50 text-emerald-700" },
  }[status] || { label: status, cls: "bg-stone-100 text-stone-600" };
  return <span className={`pill ${cfg.cls} text-[10px]`}>{cfg.label}</span>;
}

function Kpi({ label, value, mono, accent }) {
  const cls = accent === "emerald" ? "text-emerald-700" : accent === "amber" ? "text-amber-700" : accent === "ppd" ? "text-[var(--ppd)]" : accent === "stone" ? "text-stone-600" : "text-stone-900";
  return (
    <div className="bg-white border border-stone-200 rounded-xl p-3">
      <div className="text-[10px] uppercase tracking-widest text-stone-500 font-bold mb-1">{label}</div>
      <div className={`${mono ? "font-mono" : ""} text-lg font-bold ${cls}`}>{value}</div>
    </div>
  );
}


function RetrocommissionsTab({ data, onReload }) {
  const [linkCopied, setLinkCopied] = useState(false);
  const trackingToken = data.tracking_token;
  const publicUrl = trackingToken
    ? `${window.location.origin}/public/franchiseur/${trackingToken}`
    : null;

  const copyLink = async () => {
    if (!publicUrl) return;
    try {
      await navigator.clipboard.writeText(publicUrl);
      setLinkCopied(true);
      setTimeout(() => setLinkCopied(false), 2200);
    } catch (e) {
      // Fallback prompt
      window.prompt("Copie ce lien et envoie-le au franchiseur :", publicUrl);
    }
  };

  // Group by status
  const groups = useMemo(() => {
    const g = { due: [], a_venir: [], payee: [], exclu: [] };
    (data.retrocommissions || []).forEach((r) => {
      const key = r.exclu ? "exclu" : (r.status || "a_venir");
      (g[key] || g.a_venir).push(r);
    });
    return g;
  }, [data]);

  const ppdDue = (groups.due || []).reduce((s, r) => s + (r.retrocommission || 0), 0);
  const downloadPPDProforma = async () => {
    try {
      const res = await api.get(`/franchiseurs/proforma.pdf?franchiseur=${encodeURIComponent(data.franchiseur)}`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url; a.download = `proforma_retro_${data.franchiseur.replace(/\s+/g, "_")}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      alert("Échec génération proforma : " + (e.response?.data?.detail || e.message));
    }
  };

  const groupCfg = [
    { key: "due", label: "Due — à payer", color: "bg-amber-50 border-amber-200 text-amber-900", num: "text-amber-700" },
    { key: "a_venir", label: "À venir", color: "bg-stone-50 border-stone-200 text-stone-900", num: "text-stone-700" },
    { key: "payee", label: "Payée", color: "bg-emerald-50 border-emerald-200 text-emerald-900", num: "text-emerald-700" },
    { key: "exclu", label: "Retiré du calcul", color: "bg-stone-100/60 border-stone-200 text-stone-500", num: "text-stone-500" },
  ];

  return (
    <div className="space-y-5" data-testid="retros-tab">
      {/* Bandeau lien de partage */}
      <div className="card-flat p-4 flex flex-wrap items-center gap-3 bg-gradient-to-r from-rose-50/40 to-white" data-testid="retros-share-box">
        <div className="flex-1 min-w-[250px]">
          <div className="text-[10px] uppercase tracking-widest text-stone-500 font-bold flex items-center gap-1">
            <ExternalLink size={11}/> Lien de suivi pour le franchiseur
          </div>
          <div className="text-xs text-stone-600 mt-0.5">Page publique en lecture seule, sans connexion. Le franchiseur peut consulter ses rétrocommissions à venir, dues et payées.</div>
          {publicUrl && (
            <code className="text-[11px] text-stone-700 mt-1 inline-block truncate max-w-full bg-stone-100 px-2 py-0.5 rounded" data-testid="retros-public-url">{publicUrl}</code>
          )}
        </div>
        <button onClick={copyLink} disabled={!publicUrl} data-testid="retros-copy-link" className="bg-stone-900 hover:bg-black disabled:opacity-40 text-white px-4 py-2 rounded-lg text-sm font-semibold flex items-center gap-2">
          {linkCopied ? <><Check size={14}/> Copié</> : <><ExternalLink size={14}/> Copier le lien</>}
        </button>
        {publicUrl && <a href={publicUrl} target="_blank" rel="noreferrer" data-testid="retros-preview-link" className="text-[11px] text-stone-500 hover:text-stone-900 underline">Aperçu</a>}
      </div>

      {/* Génération proforma Pourquoi Pas Design (projets) */}
      <div className="card-flat p-4 flex flex-wrap items-center justify-between gap-3" data-testid="retros-ppd-proforma-box">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-stone-500 font-bold flex items-center gap-1">
            <FileDown size={11}/> Proforma Pourquoi Pas Design
          </div>
          <div className="text-xs text-stone-600 mt-0.5">Rétrocommissions projets dues à payer — {formatEur(ppdDue)}</div>
        </div>
        <button onClick={downloadPPDProforma} disabled={ppdDue <= 0} data-testid="retros-ppd-proforma"
          className="bg-[var(--ppd)] hover:brightness-110 disabled:opacity-40 text-white px-4 py-2 rounded-lg text-sm font-semibold flex items-center gap-2">
          <FileDown size={14}/> Générer la proforma PPD
        </button>
      </div>

      {/* Groupes par statut */}
      {groupCfg.map((gc) => {
        const rows = groups[gc.key];
        if (!rows || !rows.length) return null;
        const total = rows.reduce((s, r) => s + (r.retrocommission || 0), 0);
        return (
          <RetroGroup key={gc.key} cfg={gc} rows={rows} total={total} onReload={onReload}/>
        );
      })}

      {Object.values(groups).every((g) => !g.length) && (
        <div className="text-center text-stone-400 py-12 italic text-sm">Aucune rétrocommission calculée — vérifie que le taux du franchiseur est &gt; 0.</div>
      )}

      {/* Bloc Franchi'Mob distinct */}
      <FMRetroBlock franchiseur={data.franchiseur} rows={data.retrocommissions_fm || []} onReload={onReload}/>
    </div>
  );
}

function FMRetroBlock({ franchiseur, rows, onReload }) {
  const [open, setOpen] = useState(true);
  const total = rows.reduce((s, r) => s + (r.retrocommission || 0), 0);
  const totalDue = rows.filter((r) => r.status === "due").reduce((s, r) => s + (r.retrocommission || 0), 0);

  const markPaid = async (id) => { await api.post(`/franchimob/${id}/retro/pay`); onReload(); };
  const unpay = async (id) => { await api.post(`/franchimob/${id}/retro/unpay`); onReload(); };
  const downloadProforma = async () => {
    try {
      const res = await api.get(`/franchiseurs/fm-proforma.pdf?franchiseur=${encodeURIComponent(franchiseur)}`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url; a.download = `proforma_franchimob_${franchiseur.replace(/\s+/g, "_")}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      alert("Échec génération proforma : " + (e.response?.data?.detail || e.message));
    }
  };

  return (
    <div className="rounded-2xl border border-[#1E3A8A]/20 overflow-hidden" data-testid="retro-fm-block">
      <button onClick={() => setOpen((o) => !o)} className="w-full flex items-center justify-between p-4 text-left hover:bg-black/[0.02] bg-[#1E3A8A]/5">
        <div className="flex items-center gap-3">
          <ChevronRight size={14} className={`transition-transform ${open ? "rotate-90" : ""}`}/>
          <span className="pill chip-fmob text-[10px]">Franchi'Mob</span>
          <span className="font-bold uppercase tracking-widest text-xs text-[#1E3A8A]">Rétrocommissions commandes</span>
          <span className="pill bg-white/80 text-stone-700 text-[10px]">{rows.length}</span>
        </div>
        <div className="font-mono font-bold text-base text-[#1E3A8A]">{formatEur2(total)}</div>
      </button>
      {open && (
        <div className="bg-white border-t border-[#1E3A8A]/10">
          {totalDue > 0 && (
            <div className="px-4 py-2 flex justify-end">
              <button onClick={downloadProforma} data-testid="retro-fm-proforma" className="text-xs text-[#1E3A8A] font-semibold flex items-center gap-1 hover:underline">
                <FileDown size={13}/> Proforma Franchi'Mob dues ({formatEur2(totalDue)})
              </button>
            </div>
          )}
          <div className="overflow-x-auto">
            <table className="w-full text-sm min-w-[720px]">
              <thead className="bg-stone-50 text-[10px] uppercase tracking-wider text-stone-500 border-b">
                <tr>
                  <th className="px-3 py-2 text-left">Commande</th>
                  <th className="px-3 py-2 text-right">Vente HT</th>
                  <th className="px-3 py-2 text-right">Taux</th>
                  <th className="px-3 py-2 text-right">Rétro HT</th>
                  <th className="px-3 py-2 text-center">Statut</th>
                  <th className="px-3 py-2 text-center w-28">Action</th>
                </tr>
              </thead>
              <tbody>
                {rows.length === 0 && (
                  <tr><td colSpan={6} className="px-3 py-6 text-center text-stone-400 italic text-xs">Aucune commande Franchi'Mob rattachée à ce franchiseur.</td></tr>
                )}
                {rows.map((r) => (
                  <tr key={r.order_id} className="border-b border-stone-100 hover:bg-stone-50" data-testid={`fm-retro-${r.order_id}`}>
                    <td className="px-3 py-2">
                      <div className="font-semibold">#{r.numero} · {r.client}</div>
                      <div className="text-[10px] text-stone-500 max-w-[260px] truncate">{r.produit}</div>
                    </td>
                    <td className="px-3 py-2 text-right font-mono">{formatEur2(r.montant_vente)}</td>
                    <td className="px-3 py-2 text-right font-mono">{r.taux}%</td>
                    <td className="px-3 py-2 text-right font-mono font-semibold text-rose-700">{formatEur2(r.retrocommission)}</td>
                    <td className="px-3 py-2 text-center"><RetroStatusPill status={r.status}/></td>
                    <td className="px-3 py-2 text-center">
                      {r.status === "payee" ? (
                        <button onClick={() => unpay(r.order_id)} data-testid={`fm-retro-unpay-${r.order_id}`} className="text-[10px] px-2 py-0.5 border border-stone-300 rounded text-stone-600 hover:bg-stone-100">↩ Annuler</button>
                      ) : r.status === "due" ? (
                        <label className="inline-flex items-center gap-1.5 cursor-pointer text-[11px] text-stone-700">
                          <input type="checkbox" onChange={() => markPaid(r.order_id)} className="h-4 w-4 rounded border-stone-300 text-emerald-600 focus:ring-emerald-600" data-testid={`fm-retro-mark-paid-${r.order_id}`}/>
                          Payée
                        </label>
                      ) : (
                        <span className="text-[10px] text-stone-400 italic">en attente paiement</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

function RetroGroup({ cfg, rows, total, onReload }) {
  const [open, setOpen] = useState(true);
  return (
    <div className={`rounded-2xl border ${cfg.color} overflow-hidden`} data-testid={`retro-group-${cfg.key}`}>
      <button onClick={() => setOpen((o) => !o)} className="w-full flex items-center justify-between p-4 text-left hover:bg-black/[0.02]">
        <div className="flex items-center gap-3">
          <ChevronRight size={14} className={`transition-transform ${open ? "rotate-90" : ""}`}/>
          <span className="font-bold uppercase tracking-widest text-xs">{cfg.label}</span>
          <span className="pill bg-white/80 text-stone-700 text-[10px]">{rows.length}</span>
        </div>
        <div className={`font-mono font-bold text-base ${cfg.num}`}>{formatEur(total)}</div>
      </button>
      {open && (
        <div className="overflow-x-auto bg-white border-t border-current/10">
          <table className="w-full text-sm min-w-[760px]">
            <thead className="bg-stone-50 text-[10px] uppercase tracking-wider text-stone-500 border-b">
              <tr>
                <th className="px-3 py-2 text-center w-10" title="Inclus dans le calcul">Inclus</th>
                <th className="px-3 py-2 text-left">Projet</th>
                <th className="px-3 py-2 text-right">Vendu HT</th>
                <th className="px-3 py-2 text-right">Taux</th>
                <th className="px-3 py-2 text-right">Rétro HT</th>
                <th className="px-3 py-2 text-center w-28">Action</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => <RetroRow key={r.project_id} r={r} onReload={onReload}/>)}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function RetroRow({ r, onReload }) {
  const toggleExclu = async () => {
    await api.post(`/projects/${r.project_id}/retrocommission/toggle-exclu`, { exclu: !r.exclu });
    onReload();
  };
  const markPaid = async () => {
    await api.post(`/projects/${r.project_id}/retrocommission/pay`);
    onReload();
  };
  const unpay = async () => {
    await api.post(`/projects/${r.project_id}/retrocommission/unpay`);
    onReload();
  };
  return (
    <tr className={`border-b border-stone-100 hover:bg-stone-50 ${r.exclu ? "opacity-50 bg-stone-50/60" : ""}`} data-testid={`fr-retro-${r.project_id}`}>
      <td className="px-3 py-2 text-center">
        <input
          type="checkbox" checked={!r.exclu} onChange={toggleExclu}
          title={r.exclu ? "Réintégrer au calcul" : "Retirer du calcul"}
          className="h-4 w-4 rounded border-stone-300 text-[var(--ppd)] focus:ring-[var(--ppd)]"
          data-testid={`fr-retro-toggle-${r.project_id}`}
        />
      </td>
      <td className="px-3 py-2">
        <Link to={`/projets/${r.project_id}`} className={`font-semibold hover:text-[var(--ppd)] ${r.exclu ? "line-through" : ""}`}>{r.nom}</Link>
        <div className="text-[10px] text-stone-500">{r.client}</div>
      </td>
      <td className="px-3 py-2 text-right font-mono">{formatEur(r.montant_vendu)}</td>
      <td className="px-3 py-2 text-right font-mono">{r.taux}%</td>
      <td className={`px-3 py-2 text-right font-mono font-semibold ${r.exclu ? "text-stone-400" : "text-rose-700"}`}>{formatEur(r.retrocommission)}</td>
      <td className="px-3 py-2 text-center">
        {r.exclu ? (
          <span className="text-[10px] text-stone-400 italic">retiré</span>
        ) : r.status === "payee" ? (
          <button onClick={unpay} data-testid={`fr-retro-unpay-${r.project_id}`} className="text-[10px] px-2 py-0.5 border border-stone-300 rounded text-stone-600 hover:bg-stone-100">↩ Annuler</button>
        ) : (
          <label className="inline-flex items-center gap-1.5 cursor-pointer text-[11px] text-stone-700">
            <input type="checkbox" onChange={markPaid} className="h-4 w-4 rounded border-stone-300 text-emerald-600 focus:ring-emerald-600" data-testid={`fr-retro-mark-paid-${r.project_id}`}/>
            Payée
          </label>
        )}
      </td>
    </tr>
  );
}
